import React, {
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Menu, { MenuProps as MenuProperties } from '@mui/material/Menu';
import MenuItem, {
  MenuItemProps as MenuItemProperties,
} from '@mui/material/MenuItem';
import withCx, { CxProps } from 'fe-core/util/withCx';
import Box from 'fe-design-base/atoms/Box';
import Icon, { IconName } from 'fe-design-base/atoms/Icon';
import Text from 'fe-design-base/atoms/Text';

import MenuDivider from './MenuDivider';
import SimpleMenuItem from './SimpleMenuItem';
import { SubMenuItemSections } from './types';
import { useSubmenuPosition } from './useSubmenuPosition';

export interface NestedMenuProps extends Omit<MenuItemProperties, 'button'> {
  /**
   * Open state of the parent Menu
   */
  parentMenuOpen: boolean;
  /**
   * Text to be displayed on the menu item
   */
  label?: string;
  /**
   * Icon to be displayed on the left side of the menu item
   */
  iconName?: IconName;
  children?: React.ReactNode;
  tabIndex?: number;
  disabled?: boolean;
  menuProps?: Partial<Omit<MenuProperties, 'children'>>;
  dataTestId?: string;
  /**
   * Array of menu items sections to be displayed
   * If array is not passed, children ContextMenuItems should be used instead
   */
  subMenuItemSections?: SubMenuItemSections;
  /**
   * If true, a thin bottom border will be displayed on the menu item
   * @default false
   */
  menuItemProps?: Partial<
    Omit<MenuItemProperties<'li', Record<string, unknown>>, 'children'>
  >;
}
type Props = NestedMenuProps & CxProps;

const NestedMenu = React.forwardRef<HTMLLIElement | null, Props>(
  (
    {
      parentMenuOpen,
      label,
      iconName,
      children,
      tabIndex: tabIndexProp,
      menuProps,
      cx,
      disabled,
      dataTestId,
      subMenuItemSections,
    },
    ref
  ) => {
    const menuItemRef = useRef() as React.MutableRefObject<HTMLLIElement>;
    useImperativeHandle(ref, () => menuItemRef.current);

    const containerRef = useRef() as React.MutableRefObject<HTMLLIElement>;

    const menuContainerRef = useRef() as React.MutableRefObject<HTMLDivElement>;

    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

    const subMenuPosition = useSubmenuPosition(containerRef, [isSubMenuOpen]);

    const handleMouseEnter = useCallback(() => {
      setIsSubMenuOpen(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setIsSubMenuOpen(false);
    }, []);

    // Check if any immediate children are active
    const isSubmenuFocused = useCallback(() => {
      const active = containerRef?.current?.ownerDocument?.activeElement;
      const menuChildren = menuContainerRef?.current?.children;

      return menuChildren
        ? Array.from(menuChildren).some(child => child === active)
        : false;
    }, [containerRef, menuContainerRef]);

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLElement>) => {
        if (e.target === containerRef.current) {
          setIsSubMenuOpen(true);
        }
      },
      [containerRef]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
          setIsSubMenuOpen(false);
          return;
        }

        if (isSubmenuFocused()) {
          e.stopPropagation();
        }

        const active = containerRef.current?.ownerDocument?.activeElement;

        if (
          e.key === 'ArrowLeft' &&
          isSubmenuFocused() &&
          containerRef.current
        ) {
          containerRef.current.focus();
        }

        if (
          e.key === 'ArrowRight' &&
          e.target === containerRef.current &&
          e.target === active
        ) {
          const firstChild = menuContainerRef?.current?.children?.[0];
          if (firstChild) (firstChild as HTMLElement).focus();
        }
      },
      [containerRef, isSubmenuFocused, menuContainerRef]
    );

    const handleClose = useCallback(() => setIsSubMenuOpen(false), []);

    const open = isSubMenuOpen && parentMenuOpen;

    // Root element must have a `tabIndex` attribute for keyboard navigation
    let tabIndex;
    if (!disabled) {
      tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1;
    }
    return (
      <MenuItem
        component="li"
        className={cx({ disabled, open })}
        data-testid={dataTestId}
        ref={containerRef}
        onFocus={handleFocus}
        tabIndex={tabIndex}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      >
        {iconName && <Icon size="medium" iconName={iconName} />}
        <Text variant="bodyTight" color={disabled ? 'mono500' : 'mono900'}>
          {label}
        </Text>
        {!disabled && (
          <Icon size="xsmall" iconName="CaretRight" ml="auto" color="mono900" />
        )}

        <Menu
          // Set pointer events to 'none' to prevent the invisible Popover div
          // from capturing events for clicks and hovers
          // FDBContextMenu so the same styles get applied to submenus
          className="FDBContextMenu"
          style={{ pointerEvents: 'none', position: 'absolute' }}
          anchorEl={containerRef.current}
          anchorOrigin={
            subMenuPosition === 'right'
              ? { vertical: 'top', horizontal: 'right' }
              : { vertical: 'top', horizontal: 'left' }
          }
          transformOrigin={
            subMenuPosition === 'right'
              ? { vertical: 'top', horizontal: 'left' }
              : { vertical: 'top', horizontal: 'right' }
          }
          open={open}
          autoFocus={false}
          disableAutoFocus
          disableEnforceFocus
          onClose={handleClose}
          {...menuProps}
        >
          <div ref={menuContainerRef} style={{ pointerEvents: 'auto' }}>
            {typeof subMenuItemSections === 'undefined' && children}
            {subMenuItemSections &&
              subMenuItemSections.length === 1 &&
              subMenuItemSections
                .flat()
                .map((item, index) => <SimpleMenuItem key={index} {...item} />)}
            {subMenuItemSections &&
              subMenuItemSections.length > 1 &&
              subMenuItemSections.map((section, index) => (
                <Box key={index}>
                  {section.map(item => (
                    <SimpleMenuItem key={item.label} {...item} />
                  ))}
                  {index !== subMenuItemSections.length - 1 && <MenuDivider />}
                </Box>
              ))}
          </div>
        </Menu>
      </MenuItem>
    );
  }
);

export default withCx<NestedMenuProps>('FDBNestedMenu')(NestedMenu);
